import {
  AlertTriangleIcon,
  ClipboardListIcon,
  CogIcon,
  FlameIcon,
  FlaskConicalIcon,
  HardHatIcon,
  PresentationIcon,
  SearchCheckIcon,
  ShieldCheckIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  result: string;
  note: string;
  icon: LucideIcon;
  tags: string;
};

export const modules: Module[] = [
  {
    number: '01',
    title: 'Fundamentos de Seguridad e Higiene Laboral',
    subtitle: 'Comprender qué es la prevención en el trabajo',
    description:
      'Conceptos esenciales de seguridad e higiene, peligro y riesgo, exposición, accidentes e incidentes, enfermedades profesionales, cultura preventiva, condiciones y medio ambiente de trabajo, responsabilidades, prevención y principios generales. Todo el contexto que necesitás para hablar con autoridad desde el primer día.',
    result:
      'Tus alumnos entienden por qué la prevención es importante, qué dice la normativa argentina sobre seguridad laboral, cuál es la responsabilidad del trabajador y el empleador, y por qué cada medida de seguridad existe para garantizar igualdad de derechos y protección de la vida.',
    note: 'Contenido completo con recursos didácticos',
    icon: ShieldCheckIcon,
    tags: 'PELIGRO Y RIESGO · ACCIDENTES E INCIDENTES · ENFERMEDADES PROFESIONALES · CULTURA PREVENTIVA · RESPONSABILIDADES',
  },
  {
    number: '02',
    title: 'Identificación de Peligros y Evaluación de Riesgos',
    subtitle: 'Pasar de la teoría a detectar riesgos reales',
    description:
      'Métodos para observar tareas, identificar peligros, clasificar riesgos, establecer prioridades, construir matrices de riesgo, aplicar controles, jerarquía de controles, inspecciones, listas de verificación y ejercicios prácticos. Con casos argentinos.',
    result:
      'Tus alumnos pueden reconocer peligros en un ambiente de trabajo, evaluar riesgos usando métodos profesionales, establecer prioridades, diseñar controles efectivos y comprender que la identificación de riesgos es el primer paso de toda prevención.',
    note: 'Matrices y checklists listos para aplicar',
    icon: SearchCheckIcon,
    tags: 'OBSERVACIÓN DE TAREAS · MATRIZ DE RIESGO · JERARQUÍA DE CONTROLES · INSPECCIONES · LISTAS DE VERIFICACIÓN',
  },
  {
    number: '03',
    title: 'Riesgos Físicos, Químicos y Biológicos',
    subtitle: 'Conocer los agentes que dañan la salud',
    description:
      'Ruido, vibraciones, iluminación, ambiente térmico, radiaciones, sustancias químicas, etiquetado, fichas de datos de seguridad (FDS), agentes biológicos, vías de ingreso, exposición, prevención y medidas de control. Especialmente desarrollado para la realidad argentina.',
    result:
      'Tus alumnos entienden qué contaminantes existen en ambientes laborales, cómo afectan la salud, cómo identificar una sustancia peligrosa, cómo leer un etiquetado de químicos, qué protección usar y cómo prevenir enfermedades profesionales.',
    note: 'Con guía de lectura de FDS y etiquetado',
    icon: FlaskConicalIcon,
    tags: 'RUIDO Y VIBRACIONES · ILUMINACIÓN · AMBIENTE TÉRMICO · SUSTANCIAS QUÍMICAS · FDS · AGENTES BIOLÓGICOS',
  },
  {
    number: '04',
    title: 'Seguridad en Máquinas, Herramientas y Riesgo Eléctrico',
    subtitle: 'Lo esencial para talleres y espacios técnicos',
    description:
      'Prevención aplicada a máquinas, zonas peligrosas, resguardos, herramientas manuales y eléctricas, mantenimiento seguro, riesgos eléctricos, contactos directos e indirectos, puesta a tierra, procedimientos preventivos y análisis de accidentes. Con casos reales de talleres argentinos.',
    result:
      'Tus alumnos saben qué zonas de una máquina son peligrosas, cómo usar resguardos correctamente, cómo manipular herramientas de forma segura, qué es un riesgo eléctrico y cómo prevenirlo. Comprenden que la seguridad en máquinas salva vidas.',
    note: 'Casos reales de talleres argentinos',
    icon: CogIcon,
    tags: 'ZONAS PELIGROSAS · RESGUARDOS · HERRAMIENTAS · MANTENIMIENTO SEGURO · CONTACTOS DIRECTOS E INDIRECTOS · PUESTA A TIERRA',
  },
  {
    number: '05',
    title: 'Incendios, Emergencias, Evacuación y Primera Respuesta',
    subtitle: 'Preparación ante lo inesperado',
    description:
      'Prevención de incendios, teoría del fuego, clases de fuego, extintores, alarmas, señalización, vías de evacuación, planes de emergencia, roles, simulacros, actuación inicial ante emergencias y principios generales de primera respuesta. Aplicable a escuelas técnicas e instituciones educativas.',
    result:
      'Tus alumnos entienden cómo prevenir incendios, qué hacer ante una emergencia, cómo evacuar correctamente, cuáles son sus roles en un plan de emergencia, cómo usar un extintor, y comprenden que la prevención y preparación pueden salvar vidas.',
    note: 'Plan de emergencia escolar incluido',
    icon: FlameIcon,
    tags: 'TEORÍA DEL FUEGO · CLASES DE FUEGO · EXTINTORES · VÍAS DE EVACUACIÓN · PLANES DE EMERGENCIA · SIMULACROS',
  },
  {
    number: '06',
    title: 'Ergonomía, EPP, Señalización y Gestión Preventiva',
    subtitle: 'Proteger la salud integral del trabajador',
    description:
      'Ergonomía, puestos de trabajo seguro, manipulación de cargas, movimientos repetitivos, factores ergonómicos, selección y utilización de elementos de protección personal (EPP), protección ocular, auditiva, respiratoria, de manos, pies y cabeza, señalización y herramientas para fortalecer la prevención.',
    result:
      'Tus alumnos diseñan puestos de trabajo ergonómicos, saben cómo levantar cargas sin dañarse, entienden cuándo usar EPP y cuál elegir, interpretan la señalización de seguridad y comprenden que la protección del trabajador es integral.',
    note: 'Guía completa de selección de EPP',
    icon: HardHatIcon,
    tags: 'PUESTOS DE TRABAJO · MANIPULACIÓN DE CARGAS · MOVIMIENTOS REPETITIVOS · EPP · SEÑALIZACIÓN · GESTIÓN PREVENTIVA',
  },
  {
    number: '07',
    title: 'Planificación Docente y Secuencias Didácticas de Seguridad e Higiene',
    subtitle: 'Herramientas para organizar la enseñanza',
    description:
      'Recursos para organizar el ciclo lectivo, estructurar clases, desarrollar secuencias didácticas completas, estrategias de enseñanza de seguridad e higiene, trabajo con casos, actividades prácticas, evaluación de competencias preventivas y conexión con la realidad laboral.',
    result:
      'Tus alumnos participan en clases estructuradas, comprenden la lógica de la prevención desde el inicio, desarrollan proyectos significativos y transfieren lo aprendido a contextos reales de trabajo.',
    note: 'Secuencias didácticas completas',
    icon: PresentationIcon,
    tags: 'ORGANIZACIÓN DEL CICLO LECTIVO · SECUENCIAS DIDÁCTICAS · TRABAJO CON CASOS · EVALUACIÓN DE COMPETENCIAS',
  },
  {
    number: '08',
    title: '100 Casos Prácticos y Desafíos de Seguridad e Higiene',
    subtitle: 'Situaciones reales para analizar y resolver',
    description:
      '100 casos situacionales extraídos de la realidad laboral argentina: “Un trabajador realiza esta tarea…”, “Observá este escenario…”, “Identificá los peligros…”, “¿Qué medidas implementarías?”. Cada caso incluye situación, peligros identificados, análisis de riesgos, preguntas de análisis y resolución docente.',
    result:
      'Tus alumnos piensan como profesionales de la prevención, analizan situaciones complejas, toman decisiones preventivas, justifican sus respuestas con criterios técnicos y desarrollan pensamiento crítico sobre seguridad.',
    note: '100 casos con resolución docente',
    icon: AlertTriangleIcon,
    tags: 'SITUACIÓN · PELIGROS IDENTIFICADOS · ANÁLISIS DE RIESGOS · PREGUNTAS DE ANÁLISIS · RESOLUCIÓN DOCENTE',
  },
  {
    number: '09',
    title: 'Proyectos Integradores y Simulaciones Profesionales de Seguridad e Higiene',
    subtitle: 'De la teoría a la acción profesional',
    description:
      'Proyectos para integrar conocimientos mediante situaciones prácticas: diseñar un mapa de riesgos institucional, crear una campaña preventiva, elaborar un plan de evacuación real, auditar la seguridad del taller, diseñar señalización de emergencia, evaluar ergonomía escolar y simular investigación de incidentes.',
    result:
      'Tus alumnos aplican todo lo aprendido en proyectos reales, desarrollan competencias profesionales, generan productos útiles para la institución, y comprenden que la prevención es una responsabilidad compartida que requiere acción constante.',
    note: 'Proyectos y simulaciones listos para implementar',
    icon: ClipboardListIcon,
    tags: 'MAPA DE RIESGOS · CAMPAÑA PREVENTIVA · PLAN DE EVACUACIÓN · AUDITORÍA DE TALLER · INVESTIGACIÓN DE INCIDENTES',
  },
];
